/**
 * Team Spain v0 — Servicio de Estadísticas Deportivas y Revisión (M7)
 * 
 * Invoca las funciones RPC securizadas y autorizadas en Supabase (security definer con RLS y checks de equipo).
 * Gestiona:
 * - Filtros compartidos con ámbito activo.
 * - Resumen colectivo (Ataque y Defensa con exclusión de no-play/void y trazabilidad de datos faltantes).
 * - Rendimiento individual de atletas (respetando lanzador real en Hydra, target conocido vs N/D, yardas laterales N/D).
 * - Rendimiento de quintetos reales (agrupación canónica de los 5 jugadores presentes en el snap).
 * - Desglose colectivo de Ataque y Defensa por conceptos, jugadas, llamadas defensivas y partidos.
 * - Consulta de snaps contribuyentes con detalles de eventos y referencias de vídeo (Google Drive).
 * - Exportación a CSV UTF-8 compatible con Excel protegida contra inyección de fórmulas.
 */

import { supabase as rawClient } from '../../lib/supabase'
import {
  StatsFilters,
  StatsFilterOptions,
  OverviewStats,
  PlayerStatsRow,
  PlayerDetailStats,
  LineupStatsRow,
  OffenseBreakdown,
  DefenseBreakdown,
  ContributingSnap
} from '../../types/sportsStats'

const supabase = rawClient!

function serializeFilters(filters: StatsFilters): Record<string, unknown> {
  const serialized: Record<string, unknown> = {}
  if (filters.seasonId) serialized.season_id = filters.seasonId
  if (filters.eventId) serialized.event_id = filters.eventId
  if (filters.captureId) serialized.capture_id = filters.captureId
  if (filters.competitionLevel && filters.competitionLevel !== 'all') serialized.competition_level = filters.competitionLevel
  if (filters.practiceMode && filters.practiceMode !== 'all') serialized.practice_mode = filters.practiceMode
  if (filters.unit && filters.unit !== 'all') serialized.unit = filters.unit
  if (filters.startDate) serialized.start_date = filters.startDate
  if (filters.endDate) serialized.end_date = filters.endDate
  return serialized
}

export const sportsStatsService = {
  /**
   * Obtiene las opciones de filtrado disponibles para el equipo del usuario
   */
  async getFilters(teamId: string): Promise<StatsFilterOptions> {
    const { data, error } = await supabase.rpc('get_sports_stats_filters', {
      p_team_id: teamId
    })

    if (error) {
      console.error('Error fetching sports stats filters:', error)
      throw new Error(`Error al cargar filtros de estadísticas: ${error.message}`)
    }

    return (data as StatsFilterOptions) || {
      seasons: [],
      events: [],
      captures: [],
      athletes: [],
      concepts: [],
      calls: []
    }
  },

  /**
   * Obtiene el resumen global de estadísticas (Ataque, Defensa y Exclusiones de validez)
   */
  async getOverview(teamId: string, filters: StatsFilters = {}): Promise<OverviewStats> {
    const { data, error } = await supabase.rpc('get_sports_stats_overview', {
      p_team_id: teamId,
      p_filters: serializeFilters(filters)
    })

    if (error) {
      console.error('Error fetching sports stats overview:', error)
      throw new Error(`Error al obtener resumen de estadísticas: ${error.message}`)
    }

    const raw = (data || {}) as Record<string, any>
    const offense = raw.offense || {}
    const defense = raw.defense || {}
    const exclusions = raw.exclusions || {}

    return {
      snaps: {
        total: raw.total_snaps ?? raw.snaps?.total ?? 0,
        offense: offense.snaps ?? raw.snaps?.offense ?? 0,
        defense: defense.snaps ?? raw.snaps?.defense ?? 0,
        both_units: raw.snaps?.both_units ?? Math.max(0, (offense.snaps ?? 0) + (defense.snaps ?? 0) - (raw.total_snaps ?? 0)),
        offense_only: raw.snaps?.offense_only ?? Math.max(0, (raw.total_snaps ?? 0) - (defense.snaps ?? 0)),
        defense_only: raw.snaps?.defense_only ?? Math.max(0, (raw.total_snaps ?? 0) - (offense.snaps ?? 0)),
      },
      offense: {
        completions: offense.pass_completions ?? offense.completions ?? 0,
        attempts: offense.pass_attempts ?? offense.attempts ?? 0,
        comp_pct: offense.comp_pct ?? null,
        rush_attempts: offense.rush_attempts ?? 0,
        known_net_yards: offense.net_yards_total ?? offense.known_net_yards ?? 0,
        net_yards_known_snaps: offense.net_yards_known_snaps ?? 0,
        pass_tds: offense.pass_tds ?? 0,
        rush_tds: offense.rush_tds ?? 0,
        total_tds: offense.total_tds ?? 0,
        conversion_attempts: offense.conversion_attempts ?? 0,
        conversion_success: offense.conversion_success ?? 0,
        conv_pct: offense.conv_pct ?? null,
        conv_1pt_attempts: offense.conversion_1pt_attempts ?? offense.conv_1pt_attempts ?? 0,
        conv_1pt_success: offense.conversion_1pt_success ?? offense.conv_1pt_success ?? 0,
        conv_2pt_attempts: offense.conversion_2pt_attempts ?? offense.conv_2pt_attempts ?? 0,
        conv_2pt_success: offense.conversion_2pt_success ?? offense.conv_2pt_success ?? 0,
      },
      defense: {
        snaps: defense.snaps ?? 0,
        flags_success: defense.flags_success ?? 0,
        flags_miss: defense.flags_miss ?? 0,
        flag_attempts: defense.flag_attempts ?? ((defense.flags_success ?? 0) + (defense.flags_miss ?? 0)),
        flag_pct: defense.flag_pct ?? null,
        passes_defended: defense.passes_defended ?? 0,
        sacks: defense.sacks ?? 0,
        pressures: defense.pressures ?? 0,
        interceptions: defense.interceptions ?? 0,
        tds_scored: defense.tds_scored ?? 0,
        tds_conceded: defense.tds_conceded ?? 0,
        net_yards_conceded: defense.net_yards_conceded ?? 0,
        net_yards_known_snaps: defense.net_yards_known_snaps ?? 0,
        net_yards_allowed_avg: defense.net_yards_allowed_avg ?? null,
      },
      exclusions: {
        void_snaps: exclusions.void_snaps ?? 0,
        noplay_snaps: exclusions.noplay_snaps ?? 0,
        draft_snaps: exclusions.draft_snaps ?? 0,
      },
    }
  },

  /**
   * Obtiene la tabla completa de estadísticas individuales de todos los jugadores del equipo
   * (Si el llamador es un atleta, la RPC solo devuelve su propia fila autorizada).
   */
  async getAllPlayers(teamId: string, filters: StatsFilters = {}): Promise<PlayerStatsRow[]> {
    const { data, error } = await supabase.rpc('get_all_players_sports_stats', {
      p_team_id: teamId,
      p_filters: serializeFilters(filters)
    })

    if (error) {
      console.error('Error fetching all players stats:', error)
      throw new Error(`Error al obtener estadísticas de jugadores: ${error.message}`)
    }

    const rawList = (data as any[]) || []
    return rawList.map((p) => {
      const part = p.participation || {}
      const posPlayed = p.positions_played || part.positions || {}
      const pass = p.passing || {}
      const rec = p.receiving || {}
      const rush = p.rushing || {}
      const def = p.defending || {}

      return {
        athlete_id: p.athlete_id,
        first_name: p.first_name || '',
        last_name: p.last_name || '',
        dorsal_number: p.dorsal_number ?? 0,
        primary_position: p.primary_position || 'ATH',
        participation: {
          total_snaps: part.total_snaps ?? 0,
          offense_snaps: part.offense_snaps ?? 0,
          defense_snaps: part.defense_snaps ?? 0,
        },
        positions_played: posPlayed,
        passing: {
          attempts: pass.attempts ?? 0,
          completions: pass.completions ?? 0,
          comp_pct: pass.comp_pct ?? null,
          yards: pass.yards ?? null,
          tds: pass.tds ?? 0,
          interceptions: pass.interceptions ?? 0,
        },
        receiving: {
          targets: rec.targets ?? 0,
          receptions: rec.receptions ?? 0,
          rec_pct: rec.rec_pct ?? null,
          drops: rec.drops ?? 0,
          yards: rec.yards ?? null,
          tds: rec.tds ?? 0,
        },
        rushing: {
          attempts: rush.attempts ?? 0,
          yards: rush.yards ?? null,
          tds: rush.tds ?? 0,
        },
        defending: {
          flags_success: def.flags_success ?? 0,
          flags_miss: def.flags_miss ?? 0,
          flag_pct: def.flag_pct ?? null,
          passes_defended: def.passes_defended ?? 0,
          sacks: def.sacks ?? 0,
          pressures: def.pressures ?? 0,
          interceptions: def.interceptions ?? 0,
          defensive_tds: def.defensive_tds ?? 0,
        },
      }
    })
  },

  /**
   * Obtiene la ficha individual detallada de un jugador con evolución partido a partido
   */
  async getPlayerDetail(teamId: string, athleteId: string, filters: StatsFilters = {}): Promise<PlayerDetailStats> {
    const { data, error } = await supabase.rpc('get_player_sports_stats', {
      p_team_id: teamId,
      p_athlete_id: athleteId,
      p_filters: serializeFilters(filters)
    })

    if (error) {
      console.error(`Error fetching player stats for ${athleteId}:`, error)
      throw new Error(`Error al obtener ficha de jugador: ${error.message}`)
    }

    const raw = (data || {}) as Record<string, any>
    const p = raw.overview || raw.player || {}
    const part = p.participation || {}
    const posPlayed = p.positions_played || part.positions || {}
    const pass = p.passing || {}
    const rec = p.receiving || {}
    const rush = p.rushing || {}
    const def = p.defending || {}

    const normalizedOverview: PlayerStatsRow = {
      athlete_id: p.athlete_id || athleteId,
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      dorsal_number: p.dorsal_number ?? 0,
      primary_position: p.primary_position || 'ATH',
      participation: {
        total_snaps: part.total_snaps ?? 0,
        offense_snaps: part.offense_snaps ?? 0,
        defense_snaps: part.defense_snaps ?? 0,
      },
      positions_played: posPlayed,
      passing: {
        attempts: pass.attempts ?? 0,
        completions: pass.completions ?? 0,
        comp_pct: pass.comp_pct ?? null,
        yards: pass.yards ?? null,
        tds: pass.tds ?? 0,
        interceptions: pass.interceptions ?? 0,
      },
      receiving: {
        targets: rec.targets ?? 0,
        receptions: rec.receptions ?? 0,
        rec_pct: rec.rec_pct ?? null,
        drops: rec.drops ?? 0,
        yards: rec.yards ?? null,
        tds: rec.tds ?? 0,
      },
      rushing: {
        attempts: rush.attempts ?? 0,
        yards: rush.yards ?? null,
        tds: rush.tds ?? 0,
      },
      defending: {
        flags_success: def.flags_success ?? 0,
        flags_miss: def.flags_miss ?? 0,
        flag_pct: def.flag_pct ?? null,
        passes_defended: def.passes_defended ?? 0,
        sacks: def.sacks ?? 0,
        pressures: def.pressures ?? 0,
        interceptions: def.interceptions ?? 0,
        defensive_tds: def.defensive_tds ?? 0,
      },
    }

    const rawMatches = (raw.by_match as any[]) || []
    const normalizedMatches = rawMatches.map((m) => ({
      match_id: m.match_id || m.capture_id || '',
      match_title: m.match_title || m.capture_title || 'Partido',
      match_date: m.match_date || m.started_at || '',
      snaps: m.snaps ?? m.snaps_played ?? 0,
      passing_att: m.passing_att ?? 0,
      passing_cmp: m.passing_cmp ?? 0,
      passing_yards: m.passing_yards ?? null,
      passing_tds: m.passing_tds ?? 0,
      passing_int: m.passing_int ?? m.int_thrown ?? 0,
      targets: m.targets ?? 0,
      receptions: m.receptions ?? 0,
      receiving_yards: m.receiving_yards ?? m.rec_yards ?? null,
      receiving_tds: m.receiving_tds ?? m.rec_tds ?? 0,
      drops: m.drops ?? 0,
      rush_att: m.rush_att ?? 0,
      rush_yards: m.rush_yards ?? null,
      rush_tds: m.rush_tds ?? 0,
      flags_ok: m.flags_ok ?? m.flags_success ?? 0,
      flags_miss: m.flags_miss ?? 0,
      sacks: m.sacks ?? 0,
      interceptions: m.interceptions ?? 0,
    }))

    return {
      overview: normalizedOverview,
      by_match: normalizedMatches,
    }
  },

  /**
   * Obtiene el rendimiento de todos los quintetos reales (combinaciones canónicas de 5 atletas en campo)
   */
  async getLineups(teamId: string, filters: StatsFilters = {}): Promise<LineupStatsRow[]> {
    const { data, error } = await supabase.rpc('get_lineups_sports_stats', {
      p_team_id: teamId,
      p_filters: serializeFilters(filters)
    })

    if (error) {
      console.error('Error fetching lineups stats:', error)
      throw new Error(`Error al obtener estadísticas de quintetos: ${error.message}`)
    }

    const rawList = (data as any[]) || []
    return rawList.map((row) => ({
      lineup_hash: row.lineup_hash || '',
      athlete_names: Array.isArray(row.athlete_names) ? row.athlete_names : [],
      athlete_dorsals: Array.isArray(row.athlete_dorsals) ? row.athlete_dorsals : [],
      athlete_ids: Array.isArray(row.athlete_ids) ? row.athlete_ids : [],
      unit: row.unit === 'defense' ? 'defense' : 'offense',
      snap_count: row.snap_count ?? row.snaps_count ?? 0,
      match_count: row.match_count ?? row.matches_count ?? 0,
      match_names: Array.isArray(row.match_names) ? row.match_names : [],
      offense_metrics: row.offense_metrics
        ? {
            completions: row.offense_metrics.completions ?? 0,
            attempts: row.offense_metrics.attempts ?? 0,
            comp_pct: row.offense_metrics.comp_pct ?? null,
            pass_tds: row.offense_metrics.pass_tds ?? 0,
            rush_tds: row.offense_metrics.rush_tds ?? 0,
            turnovers: row.offense_metrics.turnovers ?? 0,
          }
        : undefined,
      defense_metrics: row.defense_metrics
        ? {
            flags_ok: row.defense_metrics.flags_ok ?? 0,
            flags_miss: row.defense_metrics.flags_miss ?? 0,
            flag_pct: row.defense_metrics.flag_pct ?? null,
            sacks: row.defense_metrics.sacks ?? 0,
            interceptions: row.defense_metrics.interceptions ?? 0,
            defensive_tds: row.defense_metrics.defensive_tds ?? 0,
          }
        : undefined,
    }))
  },

  /**
   * Desglose colectivo de Ataque por concepto, jugada, quinteto y partido
   */
  async getOffenseBreakdown(teamId: string, filters: StatsFilters = {}): Promise<OffenseBreakdown> {
    const { data, error } = await supabase.rpc('get_offense_breakdown_stats', {
      p_team_id: teamId,
      p_filters: serializeFilters(filters)
    })

    if (error) {
      console.error('Error fetching offense breakdown stats:', error)
      throw new Error(`Error al obtener desglose de ataque: ${error.message}`)
    }

    const raw = (data || {}) as Record<string, any>
    const byConcept = ((raw.by_concept as any[]) || []).map((c) => ({
      concept_name: c.concept_name || 'Sin concepto',
      snaps: c.snaps ?? c.snaps_count ?? 0,
      completions: c.completions ?? c.pass_completions ?? 0,
      attempts: c.attempts ?? c.pass_attempts ?? 0,
      comp_pct: c.comp_pct ?? null,
      yards: c.yards ?? c.net_yards_total ?? 0,
      tds: c.tds ?? c.tds_scored ?? 0,
      turnovers: c.turnovers ?? 0,
    }))

    const byPlay = ((raw.by_play as any[]) || []).map((p) => ({
      play_name: p.play_name || 'Sin jugada',
      concept_name: p.concept_name || 'Sin concepto',
      snaps: p.snaps ?? p.snaps_count ?? 0,
      completions: p.completions ?? p.pass_completions ?? 0,
      attempts: p.attempts ?? p.pass_attempts ?? 0,
      comp_pct: p.comp_pct ?? null,
      yards: p.yards ?? p.net_yards_total ?? 0,
      tds: p.tds ?? p.tds_scored ?? 0,
      turnovers: p.turnovers ?? 0,
    }))

    const byMatch = ((raw.by_match as any[]) || []).map((m) => ({
      match_id: m.match_id || m.capture_id || '',
      match_title: m.match_title || m.capture_title || 'Partido',
      snaps: m.snaps ?? m.snaps_count ?? 0,
      completions: m.completions ?? m.pass_completions ?? 0,
      attempts: m.attempts ?? m.pass_attempts ?? 0,
      comp_pct: m.comp_pct ?? null,
      yards: m.yards ?? m.net_yards_total ?? 0,
      tds: m.tds ?? m.tds_scored ?? 0,
      turnovers: m.turnovers ?? 0,
    }))

    return {
      by_concept: byConcept,
      by_play: byPlay,
      by_match: byMatch,
      by_lineup: (raw.by_lineup as LineupStatsRow[]) || []
    }
  },

  /**
   * Desglose colectivo de Defensa por llamada defensiva, quinteto y partido
   */
  async getDefenseBreakdown(teamId: string, filters: StatsFilters = {}): Promise<DefenseBreakdown> {
    const { data, error } = await supabase.rpc('get_defense_breakdown_stats', {
      p_team_id: teamId,
      p_filters: serializeFilters(filters)
    })

    if (error) {
      console.error('Error fetching defense breakdown stats:', error)
      throw new Error(`Error al obtener desglose de defensa: ${error.message}`)
    }

    const raw = (data || {}) as Record<string, any>
    const byCall = ((raw.by_call as any[]) || []).map((c) => ({
      call_name: c.call_name || 'Sin llamada',
      snaps: c.snaps ?? c.snaps_count ?? 0,
      flags_ok: c.flags_ok ?? 0,
      flags_miss: c.flags_miss ?? 0,
      flag_pct: c.flag_pct ?? null,
      sacks: c.sacks ?? 0,
      interceptions: c.interceptions ?? 0,
      tds_conceded: c.tds_conceded ?? 0,
    }))

    const byMatch = ((raw.by_match as any[]) || []).map((m) => ({
      match_id: m.match_id || m.capture_id || '',
      match_title: m.match_title || m.capture_title || 'Partido',
      snaps: m.snaps ?? m.snaps_count ?? 0,
      flags_ok: m.flags_ok ?? 0,
      flags_miss: m.flags_miss ?? 0,
      flag_pct: m.flag_pct ?? null,
      sacks: m.sacks ?? 0,
      interceptions: m.interceptions ?? 0,
      tds_conceded: m.tds_conceded ?? 0,
    }))

    return {
      by_call: byCall,
      by_match: byMatch,
      by_lineup: (raw.by_lineup as LineupStatsRow[]) || []
    }
  },

  /**
   * Consulta los snaps contribuyentes a una métrica seleccionada con referencias de vídeo
   */
  async getContributingSnaps(
    teamId: string,
    metricKey: string,
    params: Record<string, unknown> = {},
    filters: StatsFilters = {}
  ): Promise<ContributingSnap[]> {
    const { data, error } = await supabase.rpc('get_contributing_snaps', {
      p_team_id: teamId,
      p_metric_key: metricKey,
      p_params: params,
      p_filters: serializeFilters(filters)
    })

    if (error) {
      console.error(`Error fetching contributing snaps for ${metricKey}:`, error)
      throw new Error(`Error al obtener snaps contribuyentes: ${error.message}`)
    }

    const rawList = (data as any[]) || []
    return rawList.map((snap) => ({
      id: snap.id || snap.snap_id || '',
      capture_id: snap.capture_id || '',
      match_title: snap.match_title || snap.capture_title || 'Partido',
      sequence: snap.sequence ?? 0,
      our_side: snap.our_side === 'defense' ? 'defense' : 'offense',
      down: snap.down ?? null,
      yard_line_start: snap.yard_line_start ?? null,
      yard_line_end: snap.yard_line_end ?? null,
      net_yards: snap.net_yards ?? null,
      primary_result: snap.primary_result ?? null,
      is_touchdown: Boolean(snap.is_touchdown),
      had_lateral: Boolean(snap.had_lateral),
      points_awarded: snap.points_awarded ?? 0,
      passer_name: snap.passer_name ?? null,
      receiver_name: snap.receiver_name ?? null,
      tackler_name: snap.tackler_name ?? null,
      concept_or_call: snap.concept_or_call ?? (snap.our_side === 'defense' ? snap.defense_call_name : snap.offense_concept_name) ?? null,
      play_name: snap.play_name ?? snap.offense_play_name ?? null,
      participants: Array.isArray(snap.participants)
        ? snap.participants.map((sp: any) => ({
            athlete_id: sp.athlete_id || '',
            name: sp.name || sp.athlete_name || '',
            dorsal: sp.dorsal ?? sp.dorsal_number ?? 0,
            position: sp.position || '',
            unit: sp.unit || '',
          }))
        : [],
      events: Array.isArray(snap.events)
        ? snap.events.map((ev: any) => ({
            type: ev.type || ev.event_type || '',
            actor_name: ev.actor_name ?? null,
            target_name: ev.target_name ?? null,
            is_derived: Boolean(ev.is_derived),
          }))
        : [],
      video_url: snap.video_url ?? null,
      video_segment_id: snap.video_segment_id ?? null,
      video_timestamp_mark: snap.video_timestamp_mark ?? null,
      video_offset_seconds: snap.video_offset_seconds ?? null,
    }))
  },

  /**
   * Exporta a archivo CSV UTF-8 compatible con Excel con protección contra inyección de fórmulas.
   * - No expone datos sanitarios ni notas clínicas (separación de datos).
   * - Agrega el contexto activo de filtros y fecha/hora de exportación.
   * - Neutraliza caracteres potencialmente ejecutables (=, +, -, @).
   */
  exportToCsv(
    filename: string,
    headers: string[],
    rows: (string | number | null | undefined)[][],
    filterContextSummary: string
  ): void {
    // Sanitización anti-fórmula: si una celda de texto comienza por =, +, -, o @, se antepone un apóstrofe (')
    const sanitizeCell = (val: string | number | null | undefined): string => {
      if (val === null || val === undefined) return 'N/D'
      let str = String(val).trim()
      if (str.length > 0 && ['=', '+', '-', '@', '\t', '\r'].includes(str.charAt(0))) {
        str = `'${str}`
      }
      // Escapar comillas dobles
      str = str.replace(/"/g, '""')
      return `"${str}"`
    }

    const lines: string[] = []
    lines.push(`"TEAM SPAIN v0 — REPORTE DEPORTIVO OFICIAL"`)
    lines.push(`"Fecha de exportación: ${new Date().toLocaleString('es-ES')}"`)
    lines.push(`"Ámbito y filtros: ${filterContextSummary.replace(/"/g, '""')}"`)
    lines.push(`""`) // línea en blanco

    // Encabezados
    lines.push(headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(','))

    // Filas
    for (const row of rows) {
      lines.push(row.map(sanitizeCell).join(','))
    }

    // Unir con saltos de línea CRLF e incluir BOM UTF-8 para Excel
    const csvContent = '\uFEFF' + lines.join('\r\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    if (typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(url)
    }
  }
}
